<script lang="ts">
  /**
   * A borderless rounded block of rows, one ladder step above the ground it
   * lies on (`--ui-card`): a settings group, a table of scores, a
   * leaderboard. No border and no shadow; depth is the step. TwinTen's
   * settings group.
   *
   * Its children are `Row`s, `ToggleRow`s, `SliderRow`s. `label` is a small
   * heading *inside* the block, above its first row; a heading *between*
   * blocks is a `SectionLabel variant="sheet"`, and a sentence under a block
   * is a `GroupNote`.
   *
   * Bordered cards are for a game or a challenge (`GameCard`,
   * `ChallengeCard`). Everything list-like is a group.
   *
   * (TwinTen drew the inner label in its decoration grey; the shell reserves
   * `--ui-muted` for non-text, so the label is `--ui-ink-2`.)
   *
   * `flush` (since 1.7.0) drops the block's bottom margin, for a group
   * inside a container that spaces its children itself (a template's
   * `extra`, a column with a `gap`), where the margin would add to the gap.
   *
   * Test id: `testid` on the block.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly children: Snippet;
    readonly label?: string | undefined;
    readonly as?: 'div' | 'section';
    /** The block's accessible name, when it is a region worth naming. */
    readonly name?: string | undefined;
    readonly testid?: string | undefined;
    /** No bottom margin: the container spaces it (since 1.7.0). */
    readonly flush?: boolean;
  }
  const { children, label, as = 'div', name, testid, flush = false }: Props = $props();
</script>

<svelte:element
  this={as}
  class="ui-group"
  class:ui-group--flush={flush}
  aria-label={name}
  data-testid={testid}
>
  {#if label !== undefined}
    <div class="ui-group-label">{label}</div>
  {/if}
  {@render children()}
</svelte:element>

<style>
  .ui-group {
    box-sizing: border-box;
    margin: 0 0 var(--ui-space-3);
    padding: 0;
    background: var(--ui-card);
    border-radius: var(--ui-radius-sm);
    overflow: hidden;
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-group--flush {
    margin-bottom: 0;
  }

  .ui-group-label {
    padding: 0.5rem 1rem 0.15rem;
    font: var(--ui-text-eyebrow);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--ui-ink-2);
  }
</style>
